import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Renders `children` only once the wrapper scrolls within `rootMargin` of the
 * viewport. Used to keep heavy below-the-fold blocks (cross-link mesh, footer
 * city grids) out of the initial DOM — cuts first-paint node count on the
 * homepage from ~2,900 → ~1,500 and reduces TBT/INP on low-end phones.
 *
 * SSR-safe: server renders the placeholder, client hydrates with the
 * placeholder visible and swaps in real children on intersection.
 */
export function LazyMount({
  children,
  rootMargin = "600px 0px",
  minHeight = 200,
}: {
  children: ReactNode;
  rootMargin?: string;
  /** Reserve vertical space so swap-in doesn't cause CLS. */
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      // Old browser — just render immediately.
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} style={visible ? undefined : { minHeight }}>
      {visible ? children : null}
    </div>
  );
}
